import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPageByDynamicRouting } from "../api/pages";
import PageSections from "../sections/PageSections";
import NotFound from "../pages/NotFound";
import Lenis from "@studio-freight/lenis";
import { useSeo } from "../contexts/SeoContext";

/**
 * Smooth scroll using Lenis
 * - Same pattern as other pages
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        document.documentElement.style.scrollBehavior = "auto";

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            smoothWheel: true,
            smoothTouch: false,
        });

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
        };
    }, []);
};

/**
 * Enhanced AOS (Animate On Scroll) hook with stagger support
 */
const useAOS = (sections) => {
    useEffect(() => {
        if (!sections || sections.length === 0) return;

        let observer = null;
        const observedElements = [];

        const timer = setTimeout(() => {
            const observerOptions = {
                threshold: 0.15,
                rootMargin: "0px 0px -100px 0px",
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    requestAnimationFrame(() => {
                        if (!entry.isIntersecting) return;

                        const section = entry.target;

                        if (!section.classList.contains("aos-animate")) {
                            section.classList.add("aos-animate");
                        }

                        const staggerElements =
                            section.querySelectorAll("[data-stagger-index]");

                        staggerElements.forEach((el, idx) => {
                            const card = el.closest(".lookout-card");
                            const isLookoutCardChild = card && el !== card;

                            if (isLookoutCardChild) {
                                const staggerIndex =
                                    parseInt(el.getAttribute("data-stagger-index")) || 0;
                                const delay = 600 + staggerIndex * 150;
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, delay);
                            } else {
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, idx * 10);
                            }
                        });
                    });
                });
            }, observerOptions);

            const sectionsToObserve = document.querySelectorAll(".aos-fade-up");

            sectionsToObserve.forEach((element) => {
                if (element.classList.contains("aos-animate")) return;

                const rect = element.getBoundingClientRect();
                const isInView =
                    rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

                if (isInView) {
                    element.classList.add("aos-animate");

                    const staggerElements =
                        element.querySelectorAll("[data-stagger-index]");

                    staggerElements.forEach((el, idx) => {
                        const card = el.closest(".lookout-card");
                        const isLookoutCardChild = card && el !== card;
                        if (isLookoutCardChild) {
                            const staggerIndex =
                                parseInt(el.getAttribute("data-stagger-index")) || 0;
                            const delay = 600 + staggerIndex * 150;
                            setTimeout(() => {
                                el.classList.add("stagger-animate");
                            }, delay);
                        } else {
                            setTimeout(() => {
                                el.classList.add("stagger-animate");
                            }, idx * 10);
                        }
                    });
                } else {
                    observer.observe(element);
                    observedElements.push(element);
                }
            });
        }, 100);

        return () => {
            clearTimeout(timer);
            if (observer) {
                observedElements.forEach((element) => {
                    if (element) observer.unobserve(element);
                });
            }
        };
    }, [sections]);
};

/**
 * Attach AOS classes to all sections on this page
 */
const useWirePageSectionsForAOS = (sections) => {
    useEffect(() => {
        if (!sections || sections.length === 0) return;

        const pageSections = document.querySelectorAll(".page-section");

        pageSections.forEach((el, index) => {
            if (!el.classList.contains("aos-fade-up")) {
                el.classList.add("aos-fade-up");
            }
            if (!el.hasAttribute("data-section-index")) {
                el.setAttribute("data-section-index", String(index));
            }
        });

        const sectionEls = document.querySelectorAll("section");
        sectionEls.forEach((el) => {
            if (!el.classList.contains("aos-fade-up")) {
                el.classList.add("aos-fade-up");
            }
        });
    }, [sections]);
};

/**
 * PageResolver Component
 *
 * Dynamically resolves WordPress pages by:
 * 1. Extracting slug from URL
 * 2. Fetching pages-list from WordPress
 * 3. Finding matching page by slug
 * 4. Using the api_url to fetch page content
 * 5. Rendering the page or showing 404
 *
 * This allows WordPress to control slugs without requiring React rebuilds.
 */
export default function PageResolver() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { setPageSeo } = useSeo();

    const [sections, setSections] = useState([]);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

    // Initialize Lenis smooth scroll
    useLenis();

    // Wire AOS classes onto DOM nodes
    useWirePageSectionsForAOS(sections);

    // Initialize AOS animations
    useAOS(sections);

    useEffect(() => {
        let isMounted = true;

        const loadPage = async () => {
            if (!slug) {
                if (isMounted) {
                    setNotFound(true);

                }
                return;
            }

            try {
                setError(null);
                setNotFound(false);
                setHasAttemptedLoad(false);

                // Fetch page content directly using dynamic routing (single API call)
                // This calls: /wp-json/custom/v1/pages?dynamic-page-routing={slug}
                const pageData = await fetchPageByDynamicRouting(slug);

                if (!isMounted) return;

                if (pageData) {
                    const nextSections = Array.isArray(pageData?.page_sections)
                        ? pageData.page_sections
                        : [];
                    setSections(nextSections);

                    // Update SEO data
                    if (pageData.seo) {
                        setPageSeo(pageData.seo);
                    }

                    setError(null);
                } else {
                    setError("No data received from API");
                }
            } catch (err) {
                if (isMounted) {
                    // If it's a 404 error, show NotFound component
                    if (err.response && err.response.status === 404) {
                        setNotFound(true);
                    } else {
                        setError("Unable to load the page at the moment.");
                    }
                }
            } finally {
                if (isMounted) {
                    setHasAttemptedLoad(true);
                }
            }
        };

        loadPage();

        return () => {
            isMounted = false;
        };
    }, [slug, setPageSeo]);

    useEffect(() => {
        if (hasAttemptedLoad) {
            // wait until DOM updates with sections
            setTimeout(() => {
                window.dispatchEvent(new Event("page-ready"));
            }, 50);
        }
    }, [sections, hasAttemptedLoad]);



    // Show 404 if page not found
    if (notFound) {
        return <NotFound />;
    }

    // Error state
    if (error) {
        return <div className="page-error">{error}</div>;
    }

    // Render page content
    return (
        <div>
            {sections.length > 0 ? (
                <PageSections sections={sections} animateHero={true} />
            ) : hasAttemptedLoad ? (
                <div className="page-error">No content available for this page.</div>
            ) : null}
        </div>
    );
}

